import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import {
  analyticsConsentCookieName,
  consentCookieName,
  createDefaultConsent,
  gpcHonoredCookieName,
  gpcSignalCookieName,
  mediaConsentCookieName,
  parseStoredConsent,
  serializeConsentCookie,
} from './anonymous-state'
import type { ConsentState } from './anonymous-state'
import { getCookie, setCookie } from './cookies'
import { emitConsentChange } from './consent-events'

/**
 * Hydration-safe cookie-consent hook. This is the single source of truth for
 * whether the consent banner is shown, what the visitor previously chose, and
 * how each consent action is persisted.
 *
 * Design mirrors the gold-standard reference (kathi-product-scanner) adapted to
 * this project's first-party-cookie persistence model (no localStorage): the
 * banner state is derived through `useSyncExternalStore` so the server HTML and
 * the client's first paint always agree (no hydration mismatch / React #418),
 * then the real persisted state is surfaced once mounted.
 *
 * Global Privacy Control (Brave / DuckDuckGo / Firefox) is honoured as a legally
 * recognised opt-out signal: when GPC is present AND the visitor has no stored
 * decision we auto-apply "necessary only", persist it, and never show the
 * banner. A prior explicit decision always wins - GPC never overrides it.
 */

// How long consent decisions are remembered (in days). 180 keeps consent fresh
// without nagging returning visitors, matching the rest of the app's cookies.
const CONSENT_MAX_AGE_DAYS = 180

/**
 * Hydration gate. `getServerSnapshot` returns `false` on the server and the
 * client's first/hydration render; `getSnapshot` returns `true`. Because the
 * two differ, `useSyncExternalStore` renders the server-matching value first,
 * then re-renders to `true` right after hydration commits - so the post-mount
 * cookie read never trips a hydration mismatch.
 */
const noopSubscribe = (): (() => void) => () => {}
const getMountedSnapshot = (): boolean => true
const getMountedServerSnapshot = (): boolean => false

/**
 * True when the readable `ae_gpc=1` cookie - set by the SSR request middleware
 * (`src/start.ts`) when the request carried the `Sec-GPC: 1` header - is present.
 * The header is the AUTHORITATIVE, always-present GPC signal; the JS property
 * `navigator.globalPrivacyControl` is exposed inconsistently (notably missing on
 * Brave in many configs), so this cookie is the reliable path - including on the
 * first paint and on statically prerendered pages where the JS property is not
 * yet observable. SSR-safe: returns `false` when `document` is unavailable or
 * access throws.
 */
function hasGpcSignalCookie(): boolean {
  try {
    if (typeof document === 'undefined') return false
    // Match `ae_gpc=1` as a whole name/value pair, tolerant of surrounding
    // whitespace and other cookies before/after it.
    return new RegExp(`(?:^|;\\s*)${gpcSignalCookieName}=1(?:;|$)`).test(
      document.cookie,
    )
  } catch {
    return false
  }
}

/**
 * Read the browser's Global Privacy Control (GPC) signal. GPC is sent by Brave,
 * DuckDuckGo and Firefox (with the setting enabled) and is a legally recognised
 * opt-out signal under CCPA/CPRA and an increasingly accepted GDPR objection.
 *
 * Returns `true` when EITHER the JS property `navigator.globalPrivacyControl`
 * is `true` OR the request-header-derived `ae_gpc=1` cookie is present. The
 * cookie is the reliable path on Brave (where the JS property is often absent)
 * and on prerendered pages where the property is missing on first paint.
 *
 * Returns `false` during SSR and the first/hydration render (same hydration gate
 * as above) so server HTML and the client's first paint agree; the real value is
 * surfaced only after hydration commits.
 */
export function getGpcSnapshot(): boolean {
  try {
    const jsProperty =
      typeof navigator !== 'undefined' &&
      (navigator as Navigator & { globalPrivacyControl?: boolean })
        .globalPrivacyControl === true
    return jsProperty || hasGpcSignalCookie()
  } catch {
    return false
  }
}
const getGpcServerSnapshot = (): boolean => false

/**
 * The single source of truth for the GPC auto opt-out decision. Returns `true`
 * when - and only when - the browser is sending a GPC signal AND the visitor has
 * not already made a consent decision (no stored consent cookie). A stored
 * decision ALWAYS wins, whatever it was: GPC never overrides an explicit prior
 * choice. Exported pure so the decision can be regression-tested without a DOM.
 */
export function shouldAutoApplyGpc(
  gpc: boolean,
  storedConsent: string | ConsentState | null,
): boolean {
  return gpc && storedConsent === null
}

/**
 * Persist a consent decision across all the first-party cookies the app reads:
 * the canonical consent payload, plus the two derived per-feature flags that
 * `AnalyticsGate` and `VideoConsentGate` read directly. Centralised here so
 * every consent path (banner Accept / Necessary only / Customize, and the GPC
 * auto opt-out) writes exactly the same set of cookies.
 */
export function persistConsentCookies(consent: ConsentState): void {
  setCookie(
    consentCookieName,
    serializeConsentCookie(consent),
    CONSENT_MAX_AGE_DAYS,
  )
  setCookie(
    analyticsConsentCookieName,
    String(consent.analytics),
    CONSENT_MAX_AGE_DAYS,
  )
  setCookie(
    mediaConsentCookieName,
    String(consent.externalMedia),
    CONSENT_MAX_AGE_DAYS,
  )
  // Live consumers (PostHog runtime, Portfolio tracker) react immediately.
  emitConsentChange(consent)
}

/** Source of a persisted consent decision, used for server-side audit. */
export type ConsentSource = 'banner' | 'settings' | 'gpc'

export interface UseCookieConsentResult {
  /** The visitor's current consent state (defaults to necessary-only). */
  consent: ConsentState
  /** Whether the compact banner should be shown. */
  showBanner: boolean
  /** Whether the customization panel is expanded inside the banner. */
  settingsOpen: boolean
  setSettingsOpen: (open: boolean) => void
  /** Accept analytics + external media (marketing stays off by default). */
  acceptAll: () => void
  /** One-click reject: persist necessary-only. */
  rejectAll: () => void
  /** Persist a granular choice from the customization panel. */
  saveCustom: (next: ConsentState) => void
}

/**
 * @param onPersist optional side-channel invoked whenever a consent decision is
 *   persisted, so the caller can mirror it to the server (Convex `recordConsent`)
 *   without coupling this hook to Convex. Never called for the SSR/first render.
 */
export function useCookieConsent(
  onPersist?: (consent: ConsentState, source: ConsentSource) => void,
): UseCookieConsentResult {
  // `false` on the server + first render, flips to `true` after hydration.
  const hasMounted = useSyncExternalStore(
    noopSubscribe,
    getMountedSnapshot,
    getMountedServerSnapshot,
  )

  // `false` on the server + first render, flips to the browser's real GPC value
  // after hydration. Drives the automatic necessary-only opt-out below.
  const gpc = useSyncExternalStore(
    noopSubscribe,
    getGpcSnapshot,
    getGpcServerSnapshot,
  )

  // Runtime overrides. `null` means "no explicit runtime decision yet - derive
  // from the persisted cookie". Written only from event handlers and the
  // render-time GPC block below, never synchronously inside a `useEffect` body,
  // so SSR-safety holds and no hydration setState fires.
  const [consentOverride, setConsentOverride] = useState<ConsentState | null>(
    null,
  )
  const [showBannerOverride, setShowBannerOverride] = useState<boolean | null>(
    null,
  )
  const [settingsOpen, setSettingsOpen] = useState(false)

  // The stored, CURRENT-version decision (null before mount so server + first
  // render agree, and null for a decision recorded under an older consent
  // version: the visitor is asked again because the purposes changed).
  const stored = hasMounted
    ? parseStoredConsent(getCookie(consentCookieName))
    : null

  // Derived, hydration-safe state. Before mount both collapse to the
  // server-matching values; after mount they reflect the persisted cookie.
  const consent: ConsentState =
    consentOverride ?? stored ?? createDefaultConsent()
  const showBanner: boolean =
    showBannerOverride ?? (hasMounted && consentOverride === null && !stored)

  // ── Global Privacy Control (Brave / DuckDuckGo / Firefox) ────────────────
  // When the browser sends GPC and the visitor has NOT already made a decision,
  // treat it as an explicit opt-out: persist "necessary only" so no banner is
  // shown and no analytics/external-media scripts ever load. A one-shot latch
  // prevents re-applying on every render; the synchronous setState makes the new
  // value visible on this same render pass (React docs "Adjusting state while
  // rendering"). GPC takes precedence over the freshly-derived banner default.
  const [gpcApplied, setGpcApplied] = useState(false)
  if (
    !gpcApplied &&
    consentOverride === null &&
    shouldAutoApplyGpc(gpc, stored)
  ) {
    setGpcApplied(true)
    const prefs = createDefaultConsent()
    persistConsentCookies(prefs)
    // Record that we honoured a GPC signal (read by compliance/e2e). Kept here,
    // alongside the canonical cookies, so the GPC flag is always written
    // together with the consent it represents.
    setCookie(gpcHonoredCookieName, 'true', CONSENT_MAX_AGE_DAYS)
    setConsentOverride(prefs)
    setShowBannerOverride(false)
    onPersist?.(prefs, 'gpc')
  }

  // Allow any part of the site (the /cookies page, the footer "cookie settings"
  // link) to re-open the banner so visitors can review or withdraw consent at
  // any time - withdrawing must be as easy as granting (GDPR / ePrivacy).
  useEffect(() => {
    function openSettings() {
      setSettingsOpen(true)
      setShowBannerOverride(true)
    }
    window.addEventListener('open-cookie-settings', openSettings)
    return () =>
      window.removeEventListener('open-cookie-settings', openSettings)
  }, [])

  const persist = useCallback(
    (next: ConsentState, source: ConsentSource) => {
      persistConsentCookies(next)
      setConsentOverride(next)
      setShowBannerOverride(false)
      setSettingsOpen(false)
      onPersist?.(next, source)
    },
    [onPersist],
  )

  const acceptAll = useCallback(() => {
    persist(
      {
        version: createDefaultConsent().version,
        necessary: true,
        analytics: true,
        externalMedia: true,
        marketing: false,
      },
      'banner',
    )
  }, [persist])

  const rejectAll = useCallback(() => {
    persist(createDefaultConsent(), 'banner')
  }, [persist])

  const saveCustom = useCallback(
    (next: ConsentState) => {
      persist(next, 'settings')
    },
    [persist],
  )

  return {
    consent,
    showBanner,
    settingsOpen,
    setSettingsOpen,
    acceptAll,
    rejectAll,
    saveCustom,
  }
}
